/**
 * Phase 14.5 Step 3 — DataStarvation sub-form (controlled leaf).
 *
 * Registry-driven from `data_starvation.fields`:
 *   dropout_rate            — scalar (0..1)
 *   dropout_burst_lambda    — scalar (>0)
 *   blackout_windows        — list_of_tuple2 (start_s, end_s) — non-overlapping
 *   latency_ms_constant     — scalar (≥0)
 *   latency_ms_jitter_std   — scalar (≥0)
 *   seed                    — int
 *
 * Held to the Step-2 invariant: no internal `useForm`.
 */
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

function _setField(value, field, raw, asInt, onChange) {
    const parsed = asInt ? parseInt(raw || "0", 10) : parseFloat(raw || "0");
    onChange?.({ ...(value || {}), [field]: Number.isFinite(parsed) ? parsed : 0 });
}

function _setWindow(value, idx, axis, raw, onChange) {
    const w = (value?.blackout_windows || []).map((pair) => [...pair]);
    if (!w[idx]) w[idx] = [0, 0];
    w[idx][axis === "start" ? 0 : 1] = parseFloat(raw || "0");
    onChange?.({ ...(value || {}), blackout_windows: w });
}

function _addWindow(value, onChange) {
    const w = [...(value?.blackout_windows || []), [0, 0]];
    onChange?.({ ...(value || {}), blackout_windows: w });
}

function _removeWindow(value, idx, onChange) {
    const w = (value?.blackout_windows || []).filter((_, i) => i !== idx);
    onChange?.({ ...(value || {}), blackout_windows: w });
}

export default function DataStarvationSubForm({ value, onChange }) {
    const windows = Array.isArray(value?.blackout_windows) ? value.blackout_windows : [];
    return (
        <div className="space-y-4" data-testid="data-starvation-subform">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                    <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                        dropout_rate <span className="text-ux-text-3/60">(0..1)</span>
                    </Label>
                    <Input
                        type="number"
                        step="any"
                        min="0"
                        max="1"
                        value={value?.dropout_rate ?? 0}
                        onChange={(e) => _setField(value, "dropout_rate", e.target.value, false, onChange)}
                        data-testid="data-starvation-dropout-rate"
                    />
                </div>
                <div>
                    <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                        dropout_burst_lambda <span className="text-ux-text-3/60">(ticks, &gt;0)</span>
                    </Label>
                    <Input
                        type="number"
                        step="any"
                        min="0.01"
                        value={value?.dropout_burst_lambda ?? 1}
                        onChange={(e) => _setField(value, "dropout_burst_lambda", e.target.value, false, onChange)}
                        data-testid="data-starvation-burst-lambda"
                    />
                </div>
                <div>
                    <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                        latency_ms_constant
                    </Label>
                    <Input
                        type="number"
                        step="any"
                        min="0"
                        value={value?.latency_ms_constant ?? 0}
                        onChange={(e) => _setField(value, "latency_ms_constant", e.target.value, false, onChange)}
                        data-testid="data-starvation-latency-constant"
                    />
                </div>
                <div>
                    <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                        latency_ms_jitter_std
                    </Label>
                    <Input
                        type="number"
                        step="any"
                        min="0"
                        value={value?.latency_ms_jitter_std ?? 0}
                        onChange={(e) => _setField(value, "latency_ms_jitter_std", e.target.value, false, onChange)}
                        data-testid="data-starvation-latency-jitter"
                    />
                </div>
                <div>
                    <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                        seed
                    </Label>
                    <Input
                        type="number"
                        step="1"
                        value={value?.seed ?? 0}
                        onChange={(e) => _setField(value, "seed", e.target.value, true, onChange)}
                        data-testid="data-starvation-seed"
                    />
                </div>
            </div>

            <div data-testid="data-starvation-windows">
                <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium uppercase tracking-wide text-ux-text-3">
                        blackout_windows <span className="text-ux-text-3/60">(non-overlapping s)</span>
                    </Label>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => _addWindow(value, onChange)}
                        data-testid="data-starvation-add-window"
                    >
                        <Plus className="size-3" />
                        Add window
                    </Button>
                </div>
                <ul className="mt-2 space-y-2">
                    {windows.length === 0 && (
                        <li className="text-xs text-ux-text-3" data-testid="data-starvation-windows-empty">
                            No blackout windows configured.
                        </li>
                    )}
                    {windows.map((w, i) => (
                        <li
                            key={i}
                            className="grid grid-cols-[1fr_1fr_auto] gap-2"
                            data-testid={`data-starvation-window-row-${i}`}
                        >
                            <Input
                                type="number"
                                step="any"
                                min="0"
                                value={w[0] ?? 0}
                                placeholder="start_s"
                                onChange={(e) => _setWindow(value, i, "start", e.target.value, onChange)}
                                data-testid={`data-starvation-window-${i}-start`}
                            />
                            <Input
                                type="number"
                                step="any"
                                min="0"
                                value={w[1] ?? 0}
                                placeholder="end_s"
                                onChange={(e) => _setWindow(value, i, "end", e.target.value, onChange)}
                                data-testid={`data-starvation-window-${i}-end`}
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => _removeWindow(value, i, onChange)}
                                data-testid={`data-starvation-window-${i}-remove`}
                                aria-label={`Remove window ${i}`}
                            >
                                <X className="size-3" />
                            </Button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
